import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import logo from '../assets/imgs/DigitalContacts-logo.svg';
import Spinner from './Spinner';
import { claimCard } from '../actions/registerUser';

// Landing page for a claim link (see route/api/admin.js's
// POST /users/:id/assign and /users/:id/resend-claim, which mint these).
// Unauthenticated: there's no session yet, the token itself is the only
// credential. Validates the token first (without consuming it) so an
// already-used or expired link renders as "this link has expired"
// instead of a form that's going to fail on submit -- same reasoning as
// GET /api/users/claim/:token's own server-side comment.
const ClaimCard = ({ isAuth, user, claimCard }) => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'valid' | 'invalid'
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [rePassword, setRePassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/api/users/claim/${token}`)
      .then((res) => {
        if (cancelled) return;
        setName(res.data.name);
        setStatus('valid');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be 6 or more characters.');
      return;
    }
    if (password !== rePassword) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    setError('');
    const result = await claimCard(token, password);
    if (!result.success) {
      setError(result.msg);
      setSubmitting(false);
    }
    // On success isAuth flips true via redux and this component
    // re-renders into the isAuth branch below, same pattern as
    // Login.js's own post-login redirect.
  };

  if (isAuth) {
    if (!user) return <Spinner />;
    // A freshly-claimed pre-provisioned profile is just as unpersonalized
    // as a fresh registration (placeholder name, no avatar -- see
    // seeders/preProvisionCards.js), so it lands in the exact same
    // onboarding chain a new registrant does, not a separate one.
    return <Redirect to="/add_profile_image" />;
  }

  if (status === 'loading') return <Spinner />;

  if (status === 'invalid') {
    return (
      <div className="my-name create-account wel-back">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="logo">
                <img src={logo} alt="logo" />
              </div>
              <div className="headline">
                <h1>This link has expired</h1>
              </div>
              <div className="headline">
                <p className="p1">
                  This claim link is invalid or has already been used. Contact support if
                  you need a new one sent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-name create-account wel-back">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="logo">
              <img src={logo} alt="logo" />
            </div>
            <div className="headline">
              <h1>Welcome, {name}!</h1>
            </div>
            <div className="headline">
              <p className="p1">Set a password to activate your account.</p>
            </div>
            <form onSubmit={onSubmit}>
              {error && <p style={{ color: '#d1453b', marginBottom: '15px' }}>{error}</p>}
              <input
                type="password"
                placeholder="New Password"
                minLength={6}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ marginBottom: '15px' }}
              />
              <input
                type="password"
                placeholder="Retype Password"
                minLength={6}
                required
                value={rePassword}
                onChange={(e) => setRePassword(e.target.value)}
                style={{ marginBottom: '15px' }}
              />
              <button className="btn w-100prc" type="submit" disabled={submitting}>
                {submitting ? 'Setting up…' : 'Set Password & Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuth: state.registerUser.isAuth,
  user: state.registerUser.user,
});

export default connect(mapStateToProps, { claimCard })(ClaimCard);
